import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

import type { DomSessionRow } from '@/types/domSession';
import type { SalesAnalyticsResponse, SessionToolCall } from '@/lib/api/sessions';

import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Copy, Info, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import QualificationStatusSelect from '@/components/sessions/QualificationStatusSelect';
import SessionTranscriptTimeline from '@/components/sessions/SessionTranscriptTimeline';
import ContentViewer from '@/components/sessions/ContentViewer';
import { StatusBadge } from '@/components/platform/StatusBadge';
import { formatSourceLabel } from '@/lib/sourceLabel';
import { useUpdateSessionQualificationStatus } from '@/hooks/sessions/useUpdateSessionQualificationStatus';
import { useSalesAgent } from '@/hooks/useSalesAgent';

const formatDateTime = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds?: number) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (!minutes) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`;
};

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatQualificationStatusLabel = (value?: string) => (value ? toTitleCase(value) : 'N/A');

const formatSessionStatusLabel = (value?: string) => (value ? toTitleCase(value) : 'N/A');

const formatBookedStatusLabel = (value?: string) =>
  value?.toLowerCase() === 'booked' ? 'Booked' : 'Not Booked';

// Map qualification values to badge variant and custom styling
const getQualificationBadgeVariant = (status?: string) => {
  if (!status) return 'secondary';
  const normalized = status.toLowerCase();
  if (normalized === 'qualified') return 'default'; // Green - good fit, interested
  if (normalized === 'unqualified') return 'destructive'; // Red - not a fit
  if (normalized === 'unknown') return 'secondary'; // Yellow/Orange - needs time
  return 'outline';
};

const getSessionStatusBadgeClassName = (status?: string) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'active') return 'bg-green-100 text-green-700 border-green-200';
  if (normalized === 'inactive') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const getBookedStatusBadgeClassName = (status?: string) => {
  if ((status || '').toLowerCase() === 'booked') {
    return 'bg-green-100 text-green-700 border-green-200';
  }
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const getQualificationStatusBadgeClassName = (status?: string) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'unknown') return 'bg-gray-100 text-gray-700 border-gray-200';
  return '';
};

/** Summary with truncation and Show more/less toggle */
const SummaryContent: React.FC<{ markdown: string }> = ({ markdown }) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      setIsTruncated(contentRef.current.scrollHeight > contentRef.current.clientHeight);
    }
  }, [markdown]);

  return (
    <div>
      <div
        ref={contentRef}
        className={`prose prose-sm max-w-none text-foreground prose-strong:text-inherit prose-strong:font-medium prose-p:text-xs prose-p:leading-relaxed prose-li:text-xs ${
          !expanded ? 'line-clamp-4' : ''
        }`}
      >
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>
      {(isTruncated || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-muted-foreground hover:text-foreground mt-1.5 transition-colors"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

interface SessionDetailProps {
  /** DomSessionRow — used by the Sessions view. Optional when `analytics` is provided. */
  session?: DomSessionRow;
  /** SalesAnalyticsResponse — used by the People view. When provided, data is extracted from here. */
  analytics?: SalesAnalyticsResponse;
  /** Organization ID — used to fetch sales agent for content viewer background. */
  organizationId?: string;
  onPlayerReady?: (api: { goto: (timeInSeconds: number) => void }) => void;
  isLoadingDetail?: boolean;
  mediaLayout?: 'contained' | 'natural';
}

const SessionDetail: React.FC<SessionDetailProps> = ({
  session,
  analytics,
  organizationId,
  onPlayerReady,
  isLoadingDetail,
  mediaLayout = 'contained',
}) => {
  const isMobile = useIsMobile();
  const [activePane, setActivePane] = useState<'playback' | 'insights'>('playback');
  const [snapshotCollapsed, setSnapshotCollapsed] = useState(false);

  // Fetch sales agent for content viewer background
  const { data: salesAgent } = useSalesAgent(organizationId);
  const backgroundScreenshotUrl = salesAgent?.background_screenshot_url ?? null;

  // Determine session ID for reset effects and qualification updates
  const sessionId = analytics
    ? (analytics.sales_summary?.session_recording_id ?? '')
    : session?.id ?? '';

  useEffect(() => {
    setActivePane('playback');
  }, [sessionId]);

  // --- Data extraction: prefer `analytics` when available, else fall back to `session` ---
  const salesSummary = analytics?.sales_summary ?? (session as any)?.sales_summary;
  const summaryMarkdown = analytics
    ? (analytics.sales_summary?.summary_markdown ?? null)
    : session?.story?.session_summary?.markdown || session?.story_summary_full || session?.summary;
  const primaryIntent = analytics
    ? (analytics.sales_summary?.primary_intent || 'Unknown intent')
    : (session?.primary_intent || 'Unknown intent');
  const updatedQualificationStatus = analytics
    ? analytics.sales_summary?.updated_qualification_status
    : (salesSummary?.updated_qualification_status || (session as any)?.updated_qualification_status);
  const baseQualificationStatus = analytics
    ? (analytics.sales_summary?.qualification_status ?? '')
    : (salesSummary?.qualification_status || (session as any)?.qualification_status || '');
  const qualificationStatus = updatedQualificationStatus || baseQualificationStatus;
  const qualificationReason = analytics
    ? analytics.sales_summary?.qualification_reason
    : (salesSummary?.qualification_reason || (session as any)?.qualification_reason);
  const transcript = analytics
    ? (analytics.transcripts || [])
    : ((session as any)?.session_transcript || (session as any)?.transcripts || []);
  const toolCalls: SessionToolCall[] = analytics
    ? (analytics.tool_calls || [])
    : ((session as any)?.tool_calls || []);
  const audioUrl = analytics
    ? (analytics.sales_summary?.audio_recording_url ?? null)
    : (session?.audio_recording_url ?? salesSummary?.audio_recording_url ?? null);
  const nextSteps = analytics
    ? (analytics.sales_summary?.next_steps || [])
    : (salesSummary?.next_steps || []);
  const objections = analytics
    ? (analytics.objections || [])
    : ((session as any)?.objections || []);
  const conversationTimeline = analytics
    ? (analytics.conversation_timeline || [])
    : ((session as any)?.conversation_timeline || []);

  const updateQualificationStatus = useUpdateSessionQualificationStatus();

  // Audio sync state
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef(0);
  const [audioTime, setAudioTime] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Derive session start time from true session start when available
  const sessionStartTime = useMemo(() => {
    if (session?.started_at) return session.started_at;
    if (analytics?.sales_summary?.started_at) return analytics.sales_summary.started_at;
    if (analytics?.sales_summary?.created_at) return analytics.sales_summary.created_at;

    let earliest: string | null = null;
    for (const t of transcript) {
      if (t.timestamp && (!earliest || t.timestamp < earliest)) {
        earliest = t.timestamp;
      }
    }
    for (const tc of toolCalls) {
      if (tc.created_at && (!earliest || tc.created_at < earliest)) {
        earliest = tc.created_at;
      }
    }
    return earliest;
  }, [session, analytics, transcript, toolCalls]);

  // Calibrate timeline against audio files that start after session.start_time.
  // We anchor to the first transcript timestamp when available.
  const audioTimelineOffsetSeconds = useMemo(() => {
    if (!sessionStartTime) return 0;

    const startMs = new Date(sessionStartTime).getTime();
    if (Number.isNaN(startMs)) return 0;

    const firstTranscriptTs = transcript.find((t) => !!t.timestamp)?.timestamp;
    if (!firstTranscriptTs) return 0;

    const firstTranscriptMs = new Date(firstTranscriptTs).getTime();
    if (Number.isNaN(firstTranscriptMs)) return 0;

    return Math.max(0, (firstTranscriptMs - startMs) / 1000);
  }, [sessionStartTime, transcript]);

  const handleTimeUpdate = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (audioRef.current) {
        setAudioTime(audioRef.current.currentTime + audioTimelineOffsetSeconds);
      }
    });
  }, [audioTimelineOffsetSeconds]);

  // Clean up rAF on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handlePlay = useCallback(() => setIsAudioPlaying(true), []);
  const handlePause = useCallback(() => setIsAudioPlaying(false), []);

  // Seek handler: sets audio time (calibrated back to actual file time) and starts playback
  const handleSeek = useCallback((timeInSeconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, timeInSeconds - audioTimelineOffsetSeconds);
      audioRef.current.play().catch(() => {});
    }
  }, [audioTimelineOffsetSeconds]);

  const hasTranscriptContent = transcript.length > 0 || toolCalls.length > 0;
  const hasMediaToolCalls = toolCalls.some((tc) => {
    const type = tc.outputs_json?.type;
    return type === 'presentation_slide' || type === 'video_segment' || type === 'dom_segment' || type === 'segment_playlist';
  });

  const email = analytics
    ? (analytics.sales_summary?.email || 'Unknown')
    : (session?.userEmail || salesSummary?.email || 'Unknown');
  const geoCity = salesSummary?.geo_city ?? session?.geo_city;
  const geoRegion = salesSummary?.geo_region ?? session?.geo_region;
  const geoCountry = salesSummary?.geo_country ?? session?.geo_country;
  const location = [geoCity, geoRegion, geoCountry].filter(Boolean).join(', ') || 'Unknown';
  const browser = salesSummary?.browser ?? session?.browser ?? 'Unknown';
  const device = salesSummary?.device ?? session?.device ?? 'Unknown';
  const duration = formatDuration(salesSummary?.duration_seconds ?? session?.duration_s);
  const sourceLabel = formatSourceLabel(salesSummary?.utm_source, salesSummary?.utm_medium);
  const bookedStatus = analytics
    ? analytics.sales_summary?.booked_status
    : (salesSummary?.booked_status || (session as any)?.booked_status);
  const sessionStatus = analytics
    ? analytics.sales_summary?.session_status
    : (salesSummary?.session_status || (session as any)?.session_status);
  const sourceEnvironment = analytics
    ? (analytics.sales_summary?.source_environment ?? 'production')
    : (salesSummary?.source_environment || session?.source_environment || 'production');
  const qualificationStatusLabel = formatQualificationStatusLabel(qualificationStatus);
  const bookedStatusLabel = formatBookedStatusLabel(bookedStatus);
  const sessionStatusLabel = formatSessionStatusLabel(sessionStatus);
  const useNaturalMediaLayout = mediaLayout === 'natural';
  const startedAt = salesSummary?.started_at ?? salesSummary?.created_at ?? session?.started_at;
  const detailMeta = [
    { label: 'Started', value: formatDateTime(startedAt) },
    { label: 'Location', value: location !== 'Unknown' ? location : null },
    { label: 'Device', value: `${browser} · ${device}` },
    { label: 'Duration', value: duration !== '—' ? duration : null },
    { label: 'Source', value: sourceLabel },
  ].filter((item): item is { label: string; value: string } => Boolean(item.value));
  const sessionStatusTone = sessionStatus?.toLowerCase() === 'active' ? 'success' : 'pending';
  const bookedStatusTone = bookedStatus?.toLowerCase() === 'booked' ? 'info' : 'neutral';

  // Loading state — from People view (no data yet) or Sessions view isLoadingDetail
  if (isLoadingDetail && !analytics && !session) {
    return (
      <div className="flex h-full overflow-hidden flex-col xl:flex-row">
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b card-border shrink-0">
            <Skeleton className="h-5 w-24" />
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
        <div className="hidden w-80 border-l card-border card-tertiary-bg flex-col min-h-0 xl:flex">
          <div className="px-4 py-3 border-b card-border shrink-0">
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="p-4 space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!analytics && !session) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Select a session to view details
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden flex-col xl:flex-row">
      {/* Middle Content - Main area */}
      {(!isMobile || activePane === 'playback') && (
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative border-b border-border/80 px-4 py-3 shrink-0">
          {!isMobile && (
            <button
              type="button"
              onClick={() => setSnapshotCollapsed(!snapshotCollapsed)}
              className="absolute right-4 top-3 hidden h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground xl:inline-flex"
              aria-label={snapshotCollapsed ? 'Show insights panel' : 'Hide insights panel'}
            >
              {snapshotCollapsed ? <PanelRightOpen className="h-3.5 w-3.5" /> : <PanelRightClose className="h-3.5 w-3.5" />}
            </button>
          )}
          {isMobile ? (
            <div className="flex w-full items-center gap-2 rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setActivePane('playback')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  activePane === 'playback'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Transcript
              </button>
              <button
                type="button"
                onClick={() => setActivePane('insights')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  activePane === 'insights'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Snapshot & Moments
              </button>
            </div>
          ) : (
            <div className="flex min-w-0 flex-col gap-3 pr-9">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {email && email !== 'Unknown' ? (
                    <Link
                      to={`/people/${encodeURIComponent(email)}`}
                      className="min-w-0 break-all text-sm font-medium text-foreground hover:underline"
                    >
                      {email}
                    </Link>
                  ) : (
                    <span className="min-w-0 break-all text-sm font-medium text-foreground">{email}</span>
                  )}
                  {email && email !== 'Unknown' && (
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:text-foreground"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(email);
                        } catch (error) {
                          console.warn('Failed to copy email', error);
                        }
                      }}
                      aria-label="Copy email"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-2 xl:justify-end">
                  {sourceEnvironment === 'staging' && (
                    <StatusBadge tone="warning">Staging</StatusBadge>
                  )}
                  <StatusBadge tone={sessionStatusTone}>{sessionStatusLabel}</StatusBadge>
                  <StatusBadge tone={bookedStatusTone}>{bookedStatusLabel}</StatusBadge>
                  <QualificationStatusSelect
                    value={qualificationStatus}
                    onChange={(value) =>
                      updateQualificationStatus.mutate({
                        sessionId,
                        qualification_status: value,
                      })
                    }
                    align="end"
                    triggerType="span"
                    className={getQualificationStatusBadgeClassName(qualificationStatus)}
                  />
                </div>
              </div>
              <div className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
                {detailMeta.map((item) => (
                  <div key={item.label} className="min-w-0">
                    <span className="mr-1 text-muted-foreground">{item.label}</span>
                    <span className="break-words text-foreground/80">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto p-3">
          {/* Profile Bar */}
          {isMobile && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-2.5 shrink-0">
            <div className="flex items-center gap-2 mb-1">
              {email && email !== 'Unknown' ? (
                <Link
                  to={`/people/${encodeURIComponent(email)}`}
                  className="text-sm font-semibold text-foreground hover:underline transition-colors"
                >
                  {email}
                </Link>
              ) : (
                <span className="text-sm font-semibold text-foreground">{email}</span>
              )}
              {email && email !== 'Unknown' && (
                <button
                  type="button"
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition hover:text-foreground"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(email);
                    } catch (error) {
                      console.warn('Failed to copy email', error);
                    }
                  }}
                  aria-label="Copy email"
                >
                  <Copy className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {[location, `${browser} · ${device}`, duration, sourceLabel].filter(Boolean).join(' · ')}
            </div>
          </div>
          )}

          {/* Summary Section — always visible, truncated */}
          <div className="shrink-0 rounded-lg border border-border/80 bg-muted/20 px-3 py-2.5">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Summary</span>
            {isLoadingDetail ? (
              <div className="space-y-2">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-5/6" />
                <Skeleton className="h-3.5 w-4/6" />
              </div>
            ) : summaryMarkdown ? (
              <SummaryContent markdown={summaryMarkdown} />
            ) : (
              <p className="text-sm text-foreground">
                {primaryIntent === 'Unknown intent'
                  ? 'The user is exploring and interacting with the application.'
                  : primaryIntent}
              </p>
            )}
          </div>

          {/* Session — always visible */}
          {hasTranscriptContent && (
            <div className={`overflow-hidden rounded-lg border border-border/80 bg-background flex flex-col ${
              useNaturalMediaLayout ? 'shrink-0' : 'flex-1 min-h-0'
            }`}>
              {/* Content Viewer — locked aspect ratio */}
              <div className={`aspect-video relative shrink-0 overflow-hidden ${
                useNaturalMediaLayout ? '' : 'max-h-[38%]'
              }`}>
                <div className="absolute inset-0">
                  <ContentViewer
                    toolCalls={toolCalls}
                    audioTime={audioTime}
                    isAudioPlaying={isAudioPlaying}
                    sessionStartTime={sessionStartTime ?? undefined}
                    backgroundScreenshotUrl={backgroundScreenshotUrl}
                  />
                </div>
              </div>

              {/* Audio player */}
              {audioUrl && (
                <div className="border-b border-border bg-muted/20 px-3 py-2">
                  <audio
                    ref={audioRef}
                    controls
                    className="w-full h-8"
                    src={audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={handlePlay}
                    onPause={handlePause}
                  />
                </div>
              )}

              {/* Transcript */}
              <div className={`bg-background relative ${
                useNaturalMediaLayout ? 'h-[360px] min-h-[240px]' : 'flex-1 min-h-[200px]'
              }`}>
                <SessionTranscriptTimeline
                  transcripts={transcript}
                  toolCalls={toolCalls}
                  className="absolute inset-0 min-h-0 overflow-hidden"
                  audioCurrentTime={audioTime}
                  sessionStartTime={sessionStartTime ?? undefined}
                  onSeek={audioUrl ? handleSeek : undefined}
                  hideToolCallMedia={hasMediaToolCalls}
                />
              </div>
            </div>
          )}

          {/* Objections & Opportunities moved to Snapshot panel */}
        </div>
      </div>
      )}

      {/* Far Right Sidebar - Tabs with Snapshot and Moments */}
      {(!isMobile || activePane === 'insights') && !snapshotCollapsed && (
      <div className={`${isMobile ? 'w-full border-t border-border flex' : 'hidden xl:flex xl:w-72 xl:border-l xl:border-t-0'} bg-background flex-col min-h-0 shrink-0`}>
        {isMobile && (
          <div className="px-4 py-3 border-b card-border">
            <div className="flex w-full items-center gap-2 rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setActivePane('playback')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  activePane === 'playback'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Transcript
              </button>
              <button
                type="button"
                onClick={() => setActivePane('insights')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  activePane === 'insights'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Snapshot & Moments
              </button>
            </div>
          </div>
        )}
        <Tabs defaultValue="snapshot" className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <TabsList className="w-full !inline-flex rounded-none border-b border-border bg-transparent !h-auto !min-h-0 px-4 py-2 shrink-0 [&>div]:w-full [&>div]:!inline-flex [&>div]:gap-2 [&>div]:justify-start [&>div]:items-center [&>div]:h-full">
            <TabsTrigger
              value="snapshot"
              className="rounded-sm data-[state=active]:bg-muted px-2.5 py-1 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground h-auto"
            >
              Snapshot
            </TabsTrigger>
            <TabsTrigger
              value="moments"
              className="rounded-sm data-[state=active]:bg-muted px-2.5 py-1 text-sm font-medium text-muted-foreground data-[state=active]:text-foreground h-auto"
            >
              Moments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="snapshot" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            {isLoadingDetail ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : salesSummary ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg border border-border bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Session</span>
                    <Badge
                      variant="outline"
                      className={`capitalize ${getSessionStatusBadgeClassName(sessionStatus)}`}
                    >
                      {sessionStatusLabel}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Outcome</span>
                    <Badge
                      variant="outline"
                      className={`capitalize ${getBookedStatusBadgeClassName(bookedStatus)}`}
                    >
                      {bookedStatusLabel}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <span>Qualification</span>
                            <Info className="h-3 w-3" aria-hidden="true" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[260px] whitespace-normal text-xs leading-snug">
                          {qualificationReason || 'No qualification reason provided'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <QualificationStatusSelect
                      value={qualificationStatus}
                      onChange={(value) =>
                        updateQualificationStatus.mutate({
                          sessionId,
                          qualification_status: value,
                        })
                      }
                      align="end"
                      triggerType="span"
                      className={getQualificationStatusBadgeClassName(qualificationStatus)}
                    />
                  </div>
                </div>

                {/* Additional Qualification Metrics */}
                <div className="p-3 rounded-lg border border-border bg-muted/30 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Buying Intent</span>
                    <Badge
                      variant="outline"
                      className={`capitalize ${
                        salesSummary.buying_intent === 'high'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : salesSummary.buying_intent === 'medium'
                            ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                      }`}
                    >
                      {salesSummary.buying_intent}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Engagement</span>
                    <Badge variant="outline">
                      {toTitleCase(salesSummary.engagement_level)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Sentiment</span>
                    <Badge
                      variant="outline"
                      className={`capitalize ${
                        salesSummary.sentiment === 'positive'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : salesSummary.sentiment === 'neutral'
                            ? 'bg-gray-100 text-gray-700 border-gray-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                      }`}
                    >
                      {salesSummary.sentiment}
                    </Badge>
                  </div>
                </div>

                {/* Objections */}
                {objections && objections.length > 0 && (
                  <div className="p-3 rounded-lg border border-border bg-muted/30">
                    <span className="text-xs font-medium text-muted-foreground mb-2 block">Objections</span>
                    <ul className="space-y-1.5">
                      {objections.map((objection: any, index: number) => (
                        <li key={index} className="text-xs text-foreground leading-relaxed">
                          • {objection.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Opportunities */}
                {nextSteps && nextSteps.length > 0 && (
                  <div className="p-3 rounded-lg border border-border bg-muted/30">
                    <span className="text-xs font-medium text-muted-foreground mb-2 block">Opportunities</span>
                    <ul className="space-y-1.5">
                      {nextSteps.map((step: string, index: number) => (
                        <li key={index} className="text-xs text-foreground leading-relaxed">
                          • {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Budget Range — only show if has data */}
                {salesSummary.budget_range && (
                  <div className="p-3 rounded-lg border border-border bg-muted/30">
                    <span className="text-xs font-medium text-muted-foreground mb-1 block">Budget Range</span>
                    <p className="text-xs text-foreground">{salesSummary.budget_range}</p>
                  </div>
                )}

                {/* Timeline — only show if has data */}
                {salesSummary.timeline && (
                  <div className="p-3 rounded-lg border border-border bg-muted/30">
                    <span className="text-xs font-medium text-muted-foreground mb-1 block">Timeline</span>
                    <p className="text-xs text-foreground">{salesSummary.timeline}</p>
                  </div>
                )}

                {/* Decision Maker */}
                <div className="p-3 rounded-lg border border-border bg-muted/30">
                  <span className="text-xs font-medium text-muted-foreground mb-1 block">Decision Maker</span>
                  <p className="text-xs text-foreground">
                    {salesSummary.is_decision_maker ? 'Yes' : 'No'}
                    {salesSummary.decision_maker_role && `, ${salesSummary.decision_maker_role}`}
                  </p>
                </div>

              </div>
            ) : (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No snapshot data available
              </div>
            )}
          </TabsContent>

          <TabsContent value="moments" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            <div className="space-y-3">
              {isLoadingDetail ? (
                <>
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </>
              ) : conversationTimeline && conversationTimeline.length > 0 ? (
                <>
                  {conversationTimeline.map((milestone: any, index: number) => (
                    <div key={milestone.id || index} className="border-l-4 border-blue-500 pl-3 py-2">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-bold text-blue-600">
                          {milestone.timestamp_offset_s}s
                        </span>
                        <Badge variant="secondary" className="text-xs uppercase">
                          {milestone.milestone_type}
                        </Badge>
                      </div>
                      <p className="text-xs text-body leading-relaxed">
                        {milestone.description}
                      </p>
                      {milestone.features_mentioned && milestone.features_mentioned.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {milestone.features_mentioned.map((feature: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No moments available
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      )}
    </div>
  );
};

export default SessionDetail;
